import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PhoenixMark } from "@/components/Logo";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const poll = useCallback(async (tries) => {
    if (!sessionId) { setStatus("failed"); return; }
    if (tries >= MAX_TRIES) { setStatus("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        await checkAuth();
        return;
      }
      if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
    } catch (e) {
      console.error("Payment status poll failed:", e);
    }
    setTimeout(() => poll(tries + 1), 2000);
  }, [sessionId, checkAuth]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center bg-[#111] border border-white/10 p-10">
        {status === "checking" && (
          <>
            <PhoenixMark className="h-14 w-14 mx-auto animate-pulse" />
            <h1 className="mt-6 font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-neutral-400">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#D4AF37]" />
            <h1 className="mt-6 font-display text-2xl font-bold">Welcome to the network.</h1>
            <p className="mt-2 text-sm text-neutral-400">Your membership is active. Your new benefits are unlocked.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-7 rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold w-full" data-testid="payment-success-dashboard">Go to Dashboard</Button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-red-400" />
            <h1 className="mt-6 font-display text-2xl font-bold">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="mt-2 text-sm text-neutral-400">{status === "timeout" ? "Your payment is taking longer than expected. Check your dashboard shortly." : "We couldn't confirm your payment."}</p>
            <div className="mt-7 flex gap-3">
              <Link to="/pricing" className="flex-1"><Button variant="outline" className="w-full rounded-none border-white/20 bg-transparent text-white hover:border-[#D4AF37] hover:text-[#D4AF37]">Try again</Button></Link>
              <Link to="/dashboard" className="flex-1"><Button className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold">Dashboard</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
